import React, { useEffect, useState, useCallback, useContext } from 'react';
import axios from 'axios';
import axiosInstance from '../../utils/axiosConfig';
import Navbar from '../../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../Context/AuthContext';
import TenantContext from '../../Context/TenantContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Search } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FaCalendarAlt } from 'react-icons/fa';

interface Doctor {
  firstName: string;
  lastName: string;
}

interface AppointmentDateIST {
  day: number;
  month: string;
  year: number;
  time: string;
  fullDate?: string;
}

interface Appointment {
  _id: string;
  meetingId: string;
  doctor?: Partial<Doctor> | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  nic?: string;
  dob?: Date;
  gender?: string;
  appointment_date: string;
  appointment_date_ist?: AppointmentDateIST;
  appointment_time?: string;
  appointment_type: string;
  department: string;
  status: 'Pending' | 'Accepted' | 'Rejected';
  hasVisited?: boolean;
  address?: string;
}

const AppointmentsDashboard: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppointments, setSelectedAppointments] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all'); // 'all', 'Pending', 'Accepted', 'Rejected'
  const itemsPerPage = 5;
  const navigate = useNavigate();
  
  // Use AuthContext instead of manually extracting from localStorage
  const { user, isAuthenticated, token } = useContext(AuthContext);
  const { tenant } = useContext(TenantContext) || {};

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log('[AppointmentsDashboard] isAuthenticated:', isAuthenticated);
      console.log('[AppointmentsDashboard] User:', user?.email);
      console.log('[AppointmentsDashboard] Tenant:', tenant?.id);
      
      // Check authentication
      if (!isAuthenticated || !token || !user?.email) {
        console.log('[AppointmentsDashboard] Not authenticated, redirecting to login');
        navigate('/login');
        return;
      }
      
      console.log('[AppointmentsDashboard] Fetching appointments for email:', user.email, 'phone:', user.phone);
      
      // Fetch appointments by email first
      const emailResponse = await axiosInstance.get('/appointment/patient/email/' + user.email);
      let allAppointments = emailResponse.data.appointments || [];
      
      console.log('[AppointmentsDashboard] Appointments by email:', allAppointments.length);
      
      // If user has a phone number, also fetch appointments by phone
      if (user.phone) {
        try {
          const phoneResponse = await axiosInstance.get('/appointment/patient/phone/' + user.phone);
          const phoneAppointments = phoneResponse.data.appointments || [];
          console.log('[AppointmentsDashboard] Appointments by phone:', phoneAppointments.length);
          
          // Merge appointments, removing duplicates by _id
          const appointmentMap = new Map();
          [...allAppointments, ...phoneAppointments].forEach(apt => {
            appointmentMap.set(apt._id, apt);
          });
          allAppointments = Array.from(appointmentMap.values());
          console.log('[AppointmentsDashboard] Total unique appointments:', allAppointments.length);
        } catch (phoneError) {
          console.warn('[AppointmentsDashboard] Failed to fetch appointments by phone, continuing with email results only', phoneError);
        }
      }
      
      // Sort appointments by date (newest first)
      const sortedAppointments = allAppointments.sort((a: Appointment, b: Appointment) => {
        return new Date(b.appointment_date).getTime() - new Date(a.appointment_date).getTime();
      });
      
      console.log('[AppointmentsDashboard] Sorted appointments count:', sortedAppointments.length);
      
      setAppointments(sortedAppointments);
      setFilteredAppointments(sortedAppointments);
      setNoResults(sortedAppointments.length === 0);
      setError(null);
    } catch (error: any) {
      console.error('[AppointmentsDashboard] Error fetching appointments:', error);
      console.error('[AppointmentsDashboard] Error response:', error?.response?.data);
      setError(error?.response?.data?.message || 'Failed to load appointments. Please try again later.');
      // Redirect to login if unauthorized (401) or forbidden (403)
      if (error?.response && (error.response.status === 401 || error.response.status === 403)) {
        console.log('[AppointmentsDashboard] Auth error, redirecting to login');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, user?.email, tenant?.id, navigate]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const getStatusColor = (status: Appointment['status']) => {
    switch (status) {
      case 'Pending':
        return '#fde047'; // yellow-300
      case 'Accepted':
        return '#86efac'; // green-300
      case 'Rejected':
        return '#fca5a5'; // red-300
      default:
        return '#d1d5db'; // gray-300
    }
  };

  const handleEditClick = (appointment: Appointment) => {
    setEditingAppointment(appointment);
  };

  const handleViewAppointment = (appointment: Appointment) => {
    // Make sure to use the meetingId for routing to appointment details
    if (!appointment.meetingId) {
      console.error("Appointment missing meetingId:", appointment);
      return;
    }
    
    console.log("Navigating to appointment details with meetingId:", appointment.meetingId);
    
    // Use the correct route that matches the backend endpoint: /appointment/patient/get/:id
    navigate(`/appointment/${appointment.meetingId}`);
  };

  const handleApproveAppointment = async (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Confirm appointment on ${appointment.appointment_date}?`)) {
      return;
    }
    
    try {
      await axiosInstance.put(`/appointment/patient/update/${appointment._id}`, {
        ...appointment,
        status: 'Accepted'
      });
      
      // Update local state
      setAppointments(appointments.map(apt => 
        apt._id === appointment._id ? { ...apt, status: 'Accepted' } : apt
      ));
      setFilteredAppointments(filteredAppointments.map(apt => 
        apt._id === appointment._id ? { ...apt, status: 'Accepted' } : apt
      ));
      
      alert('Appointment confirmed successfully!');
    } catch (error: any) {
      console.error('Error approving appointment:', error);
      alert('Failed to confirm appointment. Please try again.');
    }
  };

  const handleCancelAppointment = async (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Cancel appointment on ${appointment.appointment_date}?`)) {
      return;
    }
    
    try {
      await axiosInstance.put(`/appointment/patient/update/${appointment._id}`, {
        ...appointment,
        status: 'Rejected'
      });
      
      // Update local state
      setAppointments(appointments.map(apt => 
        apt._id === appointment._id ? { ...apt, status: 'Rejected' } : apt
      ));
      setFilteredAppointments(filteredAppointments.map(apt => 
        apt._id === appointment._id ? { ...apt, status: 'Rejected' } : apt
      ));
      
      alert('Appointment cancelled successfully!');
    } catch (error: any) {
      console.error('Error cancelling appointment:', error);
      alert('Failed to cancel appointment. Please try again.');
    }
  };

  const handleSelectChange = (e: any) => {
    if (editingAppointment && e.target.name) {
      setEditingAppointment({
        ...editingAppointment,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editingAppointment && e.target.name) {
      setEditingAppointment({
        ...editingAppointment,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSaveClick = async () => {
    if (editingAppointment) {
      try {
        const token = localStorage.getItem('token');
        await axiosInstance.put(`/appointment/patient/update/${editingAppointment._id}`,
          editingAppointment
        );
        setAppointments(appointments.map(appointment =>
          appointment._id === editingAppointment._id ? editingAppointment : appointment
        ));
        setEditingAppointment(null);
      } catch (error: any) {
        console.error('Error updating appointment:', error);
      }
    }
  };

  const closeModal = () => {
    setEditingAppointment(null);
  };

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);  // Format: "YYYY-MM-DDThh:mm"
  };

  // Date helper functions
  const getAppointmentDisplayDetails = (appointment: Appointment) => {
    const rawDate = appointment.appointment_date || '';
    const [datePart = '', timePartRaw = ''] = rawDate.split('T');
    const [yearStr = '', monthStr = '', dayStr = ''] = datePart.split('-');
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = monthStr ? Number(monthStr) - 1 : -1;
    const month = MONTHS[monthIndex] || monthStr || '';
    const day = dayStr ? parseInt(dayStr, 10) : NaN;
    const displayDay = dayStr ? dayStr.padStart(2, '0') : '';
    const parsedYear = parseInt(yearStr, 10);
    const year = Number.isFinite(parsedYear) ? parsedYear : undefined;
    const rawTime = (appointment.appointment_time || timePartRaw.replace(/Z$/, '')).trim();
    const time = rawTime;
    const dateParts: string[] = [];
    if (displayDay && month) {
      dateParts.push(`${displayDay} ${month}`);
    } else if (displayDay) {
      dateParts.push(displayDay);
    } else if (month) {
      dateParts.push(month);
    }
    if (Number.isFinite(year)) {
      dateParts.push(String(year));
    }
    const dateSegment = dateParts.join(' ');
    const display = dateSegment && time ? `${dateSegment} at ${time}` : dateSegment || time;

    return {
      day: Number.isFinite(day) ? day : undefined,
      month,
      year,
      time,
      display,
    };
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
  };

  const handleSearch = () => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);

      let filtered = appointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.appointment_date);
        return appointmentDate >= from && appointmentDate <= to;
      });

      // Apply status filter
      if (selectedFilter !== 'all') {
        filtered = filtered.filter(appointment => appointment.status === selectedFilter);
      }
      // Apply text search (patient, email, phone, department, status, meetingId)
      const q = (searchTerm || '').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter((a) => {
          const fields = [
            a.firstName,
            a.lastName,
            a.email,
            a.phone,
            a.department,
            a.status,
            a.meetingId,
          ].map(v => (v || '').toString().toLowerCase());
          return fields.some(f => f.includes(q));
        });
      }
      setFilteredAppointments(filtered);
      setNoResults(filtered.length === 0);
    } else {
      // No date filter: apply status and search only
      let filtered = [...appointments];
      if (selectedFilter !== 'all') {
        filtered = filtered.filter(appointment => appointment.status === selectedFilter);
      }
      const q = (searchTerm || '').toLowerCase().trim();
      if (q) {
        filtered = filtered.filter((a) => {
          const fields = [
            a.firstName,
            a.lastName,
            a.email,
            a.phone,
            a.department,
            a.status,
            a.meetingId,
          ].map(v => (v || '').toString().toLowerCase());
          return fields.some(f => f.includes(q));
        });
      }
      setFilteredAppointments(filtered);
      setNoResults(filtered.length === 0);
    }
  };

  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setSelectedFilter('all');
    setSearchTerm('');
    setFilteredAppointments(appointments);
    setNoResults(appointments.length === 0);
  };

  // Auto-apply filters when inputs change
  useEffect(() => {
    handleSearch();
  }, [searchTerm, selectedFilter, fromDate, toDate, appointments]);

  // no date presets; custom range only

  const setQuickDateRange = (range: string) => {
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split('T')[0];
    let fromDateValue: string, toDateValue: string;
    
    switch (range) {
      case 'today':
        fromDateValue = formatDate(today);
        toDateValue = formatDate(today);
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDateValue = formatDate(weekStart);
        toDateValue = formatDate(today);
        break;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDateValue = formatDate(monthStart);
        toDateValue = formatDate(today);
        break;
      default:
        return;
    }
    
    // Set the dates
    setFromDate(fromDateValue);
    setToDate(toDateValue);
    
    // Automatically trigger search after setting dates
    setTimeout(() => {
      const from = new Date(fromDateValue);
      const to = new Date(toDateValue);
      to.setHours(23, 59, 59, 999);

      let filtered = appointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.appointment_date);
        return appointmentDate >= from && appointmentDate <= to;
      });

      // Apply status filter
      if (selectedFilter !== 'all') {
        filtered = filtered.filter(appointment => appointment.status === selectedFilter);
      }

      setFilteredAppointments(filtered);
      setNoResults(filtered.length === 0);
    }, 100);
  };

  const handleCardFilter = (filterType: string) => {
    setSelectedFilter(filterType);
    
    let filtered = appointments;
    
    // Apply date filter if dates are set
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      filtered = appointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.appointment_date);
        return appointmentDate >= from && appointmentDate <= to;
      });
    }
    
    // Apply status filter
    if (filterType !== 'all') {
      filtered = filtered.filter(appointment => appointment.status === filterType);
    }
    
    setFilteredAppointments(filtered);
    setNoResults(filtered.length === 0);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const paginatedAppointments = filteredAppointments.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Add this helper function before the return statement
  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
  
    if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    }
  
    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
  
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <CircularProgress color="primary" />
          <p className="mt-4 text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div className=" bg-gray-100">
      <Navbar />
      
      <div className="flex flex-col items-center p-2 sm:p-5">
        <div className="w-full max-w-4xl bg-white rounded-3xl shadow-md">
          <div className="p-3 sm:p-6">
            <h2 className="text-2xl font-semibold text-gray-900 text-center mb-4 sm:mb-6">Appointments</h2>

            {/* Compact Rounded Filter Toolbar */}
            <div className="w-full flex flex-col gap-3 mb-4 sm:mb-5">
              {/* Top Row: Search Bar (Full Width) */}
              <div className="relative w-full">
                <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                  <Search sx={{ fontSize: 20 }} />
                </div>
                <input
                  type="text"
                  placeholder="Search appointments (name, email, phone, department, status, meeting ID...)"
                  className="h-11 w-full pl-11 pr-4 rounded-full border-2 border-gray-200 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-white shadow-sm transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search"
                />
              </div>

              {/* Middle Row: Status Filter Pills with Counts */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-gray-600 mr-1">Status:</span>
                <button
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm ${
                    selectedFilter === 'all'
                      ? 'bg-blue-500 text-white border-2 border-blue-600 shadow-md'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow'
                  }`}
                  onClick={() => setSelectedFilter('all')}
                >
                  All ({appointments.length})
                </button>
                <button
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm ${
                    selectedFilter === 'Pending'
                      ? 'bg-yellow-400 text-yellow-900 border-2 border-yellow-500 shadow-md'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow'
                  }`}
                  onClick={() => setSelectedFilter('Pending')}
                >
                  Pending ({appointments.filter(a => a.status === 'Pending').length})
                </button>
                <button
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm ${
                    selectedFilter === 'Accepted'
                      ? 'bg-green-400 text-green-900 border-2 border-green-500 shadow-md'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow'
                  }`}
                  onClick={() => setSelectedFilter('Accepted')}
                >
                  Accepted ({appointments.filter(a => a.status === 'Accepted').length})
                </button>
                <button
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm ${
                    selectedFilter === 'Rejected'
                      ? 'bg-red-400 text-red-900 border-2 border-red-500 shadow-md'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:shadow'
                  }`}
                  onClick={() => setSelectedFilter('Rejected')}
                >
                  Rejected ({appointments.filter(a => a.status === 'Rejected').length})
                </button>
              </div>

              {/* Bottom Row: Date Range Filter */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-100 rounded-lg sm:rounded-full p-2 shadow-sm">
                <div className="flex items-center gap-2 flex-1">
                  <FaCalendarAlt className="text-blue-500 ml-2 hidden sm:block" />
                  <input
                    type="date"
                    className="h-9 px-3 rounded-full border-2 border-gray-200 text-xs flex-1 bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    aria-label="From Date"
                  />
                  <span className="text-gray-400 font-bold">→</span>
                  <input
                    type="date"
                    className="h-9 px-3 rounded-full border-2 border-gray-200 text-xs flex-1 bg-white focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all"
                    value={toDate}
                    onChange={handleToDateChange}
                    aria-label="To Date"
                  />
                </div>

                <div className="flex gap-2 sm:ml-2">
                  <button
                    className="h-9 px-4 bg-blue-500 text-white text-xs font-medium rounded-full hover:bg-blue-600 active:scale-95 flex items-center justify-center gap-1.5 shadow-sm transition-all flex-1 sm:flex-none"
                    onClick={handleSearch}
                    aria-label="Apply filters"
                  >
                    <FilterListIcon sx={{ fontSize: 16 }} />
                    Filter
                  </button>
                  <button
                    className="h-9 px-4 bg-gray-400 text-white text-xs font-medium rounded-full hover:bg-gray-500 active:scale-95 shadow-sm transition-all flex-1 sm:flex-none"
                    onClick={handleReset}
                    aria-label="Reset filters"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Active filter chips */}
              {(selectedFilter !== 'all' || (fromDate && toDate) || searchTerm) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Active filters:</span>
                  {selectedFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 text-xs font-medium rounded-full border border-blue-200 shadow-sm">
                      Status: {selectedFilter}
                      <button 
                        className="ml-0.5 text-blue-700 hover:text-blue-900 font-bold" 
                        onClick={() => setSelectedFilter('all')}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {fromDate && toDate && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-xs font-medium rounded-full border border-green-200 shadow-sm">
                      📅 {new Date(fromDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} → {new Date(toDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      <button 
                        className="ml-0.5 text-green-700 hover:text-green-900 font-bold" 
                        onClick={() => { setFromDate(''); setToDate(''); }}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs font-medium rounded-full border border-purple-200 shadow-sm">
                      🔍 "{searchTerm.substring(0, 20)}{searchTerm.length > 20 ? '...' : ''}"
                      <button 
                        className="ml-0.5 text-purple-700 hover:text-purple-900 font-bold" 
                        onClick={() => setSearchTerm('')}
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          
            {/* Loading indicator */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <CircularProgress color="primary" size={24} />
                  <p className="mt-4 text-gray-500">Loading appointments...</p>
                </div>
              </div>
            )}

            {/* No results message */}
            {!loading && noResults && (
              <div className="text-center py-10 bg-gray-50 rounded-lg">
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/7486/7486744.png" 
                  alt="No data" 
                  className="w-16 h-16 mx-auto mb-4 opacity-50"
                />
                <h3 className="text-xl font-medium text-gray-600 mb-1">No appointments found</h3>
                <p className="text-gray-500">
                  {fromDate && toDate 
                    ? `No appointments between ${new Date(fromDate).toLocaleDateString()} and ${new Date(toDate).toLocaleDateString()}`
                    : 'No appointments are available in your history'}
                </p>
              </div>
            )}

            {/* Appointment list */}
            {!loading && !noResults && (
              <div className="overflow-hidden">
                <table className="w-full text-sm text-left text-gray-500">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3">Date</th>
                      <th scope="col" className="px-4 py-3">Patient</th>
                      <th scope="col" className="px-4 py-3 text-center">Status</th>
                      <th scope="col" className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((appointment, index) => {
                      const displayDetails = getAppointmentDisplayDetails(appointment);
                      return (
                        <tr 
                          key={appointment._id}
                          className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleViewAppointment(appointment)}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <div className="w-12 h-12 rounded-full bg-blue-100 flex flex-col items-center justify-center mr-3">
                                <div className="text-sm font-bold">{displayDetails.day}</div>
                                <div className="text-xs">{displayDetails.month}</div>
                              </div>
                              <span className="text-xs text-gray-500">
                                <p className='text-lg font-bold'>{appointment.appointment_type}</p>
                                {displayDetails.display}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-800">
                              {appointment.firstName} {appointment.lastName}
                            </div>
                            <div className="text-xs text-gray-500">{appointment.email}</div>
                            {appointment.phone && (
                              <div className="text-xs text-gray-500">📱 {appointment.phone}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span 
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                appointment.status === 'Pending' 
                                  ? 'bg-yellow-100 text-yellow-800' 
                                  : appointment.status === 'Accepted'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {appointment.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <div className="flex justify-center space-x-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewAppointment(appointment);
                                }}
                                className="text-blue-600 hover:text-blue-800"
                                title="View Appointment"
                              >
                                <VisibilityIcon fontSize="small" />
                              </button>
                              {appointment.status === 'Pending' && (
                                <>
                                  <button 
                                    onClick={(e) => handleApproveAppointment(appointment, e)}
                                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                    title="Confirm Appointment"
                                  >
                                    ✓ Confirm
                                  </button>
                                  <button 
                                    onClick={(e) => handleCancelAppointment(appointment, e)}
                                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                    title="Cancel Appointment"
                                  >
                                    ✗ Cancel
                                  </button>
                                </>
                              )}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(appointment);
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Edit Appointment"
                              >
                                <EditIcon fontSize="small" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingAppointment} onClose={closeModal}>
        {editingAppointment && (
          <>
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
              <TextField
                label="Patient First Name"
                name="firstName"
                value={editingAppointment.firstName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Patient Last Name"
                name="lastName"
                value={editingAppointment.lastName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={editingAppointment.status}
                  onChange={handleSelectChange}
                  label="Status"
                >
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Accepted">Accepted</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Appointment Date"
                name="appointment_date"
                type="datetime-local"
                value={formatDateForInput(editingAppointment.appointment_date)}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                onClick={handleSaveClick}
                variant="contained"
                sx={{ bgcolor: '#22c55e' }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default AppointmentsDashboard;
